import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Card } from "../shared/Card";
import { FormRow } from "../shared/FormLayout/FormRow";
import { MediaButton } from "../shared/FormLayout/IconButtons/MediaButton";

import { UTP2Conditions, UTP2Groups, UTP2Substations } from "../shared/enums/UTP2";
import { IdName } from "../shared/interfaces/Asset";
import { Result, ResultSurveyableAsset, SurveyFields } from "../shared/interfaces/Result";

import { Stack } from "@mui/material";
import { managedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { CopyToClipboard } from "../shared/CopyToClipboard";
import { getSubstationDisplayName } from "../shared/helpers/ResultUtils";
import { assetTypeFieldMapping } from "../shared/interfaces/SurveyableAsset";
import { ResultComments } from "./Comments/ResultComments";
import { CustomFields } from "./CustomFields";
import { UploadImageButton } from "./UploadImageButton";

interface Props {
  result?: Result;
  isPartialResult: boolean;
  pageLoading?: boolean;
  enableMediaIcon?: boolean;
  onMediaClick: (asset?: IdName) => void;
  customerId?: number;
  canAddResult?: boolean;
  onImageUploadSuccess?: () => void;
  onImageUploadError?: (response: Response) => Promise<void>;
  selectedAsset: ResultSurveyableAsset | undefined;
}

export interface ResultMetadataData {
  label: string;
  value: string | JSX.Element | undefined;
  valueText?: string;
}

const TAB_SPACE = "	";

export function constructClipboardText(data?: ResultMetadataData[], customFields?: SurveyFields[]) {
  let regularFieldsText = "";
  if (data && data.length > 0)
    regularFieldsText = data.map((d) => `${d.label}, ${d.valueText != null ? d.valueText : d.value}`).join("\n");

  let customFieldsText = "";
  if (customFields && customFields.length > 0) {
    customFieldsText = customFields
      .map((field) => {
        const fieldData = field.fields
          .map((subfield) => TAB_SPACE + subfield.fieldname + ", " + subfield.data)
          .join("\n");
        return "\n" + field.group + "\n" + fieldData;
      })
      .join("\n");
  }

  return [regularFieldsText, customFieldsText].filter((text) => text).join("\n");
}

export function ResultMetadata(props: Props) {
  const { t } = useTranslation();
  const data = props.result?.dataValue;
  const metaData = data?.MetaData;
  const customerId = props.customerId;
  const { canAddResult, onImageUploadSuccess, onImageUploadError } = props;

  const substationDisplayName = props.result && getSubstationDisplayName(props.result);

  const handleImageUploadSuccess = useCallback(() => {
    onImageUploadSuccess?.();
  }, [onImageUploadSuccess]);

  const handleImageUploadError = useCallback(
    async (response: Response) => {
      await onImageUploadError?.(response);
    },
    [onImageUploadError],
  );

  const resultOverviewData = useMemo(
    () => [
      {
        label: t(`results.resultDate`),
        value: t("common.formattedDate", { dateString: props.result?.time }),
      },
      {
        label: t(`common.jobNumber`),
        value: metaData?.JobGroup?.JobNo,
      },
      {
        label: t(`common.engineerName`),
        value: metaData?.JobGroup?.EngineerName,
      },
    ],
    [props.result, metaData, t],
  );

  const substationData = useMemo(
    () => [
      {
        label: t(`substations.substationName`),
        value: props.result ? getSubstationDisplayName(props.result) : "",
      },
      {
        label: t(`substations.substationType`),
        value: metaData?.SubstationGroup?.SubstationType
          ? t(`UTP2.substationType.${metaData?.SubstationGroup.SubstationType}`)
          : "",
      },
      {
        label: t(`UTP2.${UTP2Groups.Substation}.${UTP2Substations.CableNaming}`),
        value:
          metaData?.SubstationGroup?.CableNamingScheme &&
          t(`UTP2.cableNaming.${metaData?.SubstationGroup.CableNamingScheme}`),
      },
      {
        label: t(`common.media`),
        value: (
          <Stack direction={"row"} spacing={1}>
            <MediaButton
              onClick={() =>
                props.result &&
                metaData &&
                substationDisplayName &&
                props.onMediaClick({
                  id: props.result.assetId,
                  name: substationDisplayName,
                })
              }
              disabled={!props.enableMediaIcon}
              tooltip={
                props.enableMediaIcon
                  ? `${t("common.viewMediaFor", { location: substationDisplayName })}`
                  : `${t("common.noMediaFor", { location: substationDisplayName })}`
              }
            />
            {props.result && props.result.captureId && metaData && substationDisplayName && canAddResult && (
              <UploadImageButton
                result={props.result}
                captureId={props.result?.captureId}
                surveyAssetName={substationDisplayName || ""}
                onUploadSuccess={handleImageUploadSuccess}
                onUploadError={handleImageUploadError}
              />
            )}
          </Stack>
        ),
        valueText: props.enableMediaIcon
          ? `${t("common.viewMediaFor", { location: substationDisplayName })}`
          : `${t("common.noMediaFor", { location: substationDisplayName })}`,
      },
    ],
    [props, metaData, substationDisplayName, canAddResult, t, handleImageUploadSuccess, handleImageUploadError],
  );

  const assetGroup =
    metaData?.HvSurveyableAssetGroups?.find((group) => group.AssetName === props.selectedAsset?.AssetName) ??
    metaData?.HvSurveyableAssetGroups?.[0];
  const assetType =
    props.selectedAsset?.AssetType === managedSurveyAssetType.Unknown
      ? managedSurveyAssetType.Switchgear
      : (props.selectedAsset?.AssetType ?? managedSurveyAssetType.Switchgear);

  const fieldsToRender = assetTypeFieldMapping[assetType].map((field) => t(`switchgear.${field}`)) || [];

  const switchgearData = useMemo(
    () => [
      {
        label: t(`switchgear.manufacturer`),
        value: assetGroup?.Manufacturer,
      },
      {
        label: t(`switchgear.type`),
        value: assetGroup?.Type,
      },
      {
        label: t(`switchgear.installDate`),
        value: assetGroup?.InstallDate,
      },
      {
        label: t(`switchgear.ratedVoltage`),
        value: assetGroup?.RatedVoltage,
      },
      {
        label: t(`switchgear.operatingVoltage`),
        value: assetGroup?.OperatingVoltage,
      },
      {
        label: t(`switchgear.insulation`),
        value: assetGroup?.Insulation && t(`UTP2.insulationType.${assetGroup?.Insulation}`),
      },
    ],
    [assetGroup, t],
  );

  const conditionsData = useMemo(
    () => [
      {
        label: t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackMetalDbReading}`),
        value: metaData?.SubstationConditionsGroup?.BackMetalDbReading,
      },
      {
        label: t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackMetalPPCReading}`),
        value: metaData?.SubstationConditionsGroup?.BackMetalPPCReading,
      },
      {
        label: t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackAirDbReading}`),
        value: metaData?.SubstationConditionsGroup?.BackAirDbReading,
      },
      {
        label: t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackAirPPCReading}`),
        value: metaData?.SubstationConditionsGroup?.BackAirPPCReading,
      },
      {
        label: t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.TemperatureCelsius}`),
        value: metaData?.SubstationConditionsGroup?.TemperatureCelsius,
      },
      {
        label: t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.RelativeHumidity}`),
        value: metaData?.SubstationConditionsGroup?.RelativeHumidity,
      },
    ],
    [metaData, t],
  );

  const conditionCustomFields = useMemo(() => {
    return metaData?.SubstationConditionsGroup?.CustomQuestions &&
      metaData?.SubstationConditionsGroup?.CustomQuestions.length > 0
      ? [
          {
            group: t("UTP2.customConditionsQuestions"),
            fields: metaData?.SubstationConditionsGroup?.CustomQuestions,
          },
        ]
      : undefined;
  }, [metaData, t]);

  return (
    <React.Fragment>
      <Card
        title={t("results.resultOverview")}
        className="mb-3"
        loading={props.pageLoading}
        action={<CopyToClipboard textToCopy={constructClipboardText(resultOverviewData)} />}
      >
        {resultOverviewData.map((d) => (
          <FormRow key={d.label} label={d.label} value={d.value} />
        ))}
      </Card>
      {!props.isPartialResult && (
        <Card title={t("common.comments")} className="mb-3" loading={props.pageLoading}>
          {props.result && <ResultComments captureId={props.result?.captureId} customerId={customerId} />}
        </Card>
      )}
      <Card
        title={t("substations.substation")}
        className="mb-3"
        loading={props.pageLoading}
        action={<CopyToClipboard textToCopy={constructClipboardText(substationData)} />}
      >
        {substationData.map((d) => (
          <FormRow key={d.label} label={d.label} value={d.value} />
        ))}
      </Card>
      <Card
        title={t("asset.assetDetails")}
        className="mb-3"
        loading={props.pageLoading}
        action={<CopyToClipboard textToCopy={constructClipboardText(switchgearData)} />}
      >
        {switchgearData
          .filter((data) => fieldsToRender.includes(data.label))
          .map((d) => (
            <FormRow key={d.label} label={d.label} value={d.value} />
          ))}
      </Card>
      <Card
        title={t("UTP2.conditions")}
        className="mb-3"
        loading={props.pageLoading}
        action={<CopyToClipboard textToCopy={constructClipboardText(conditionsData, conditionCustomFields)} />}
      >
        {conditionsData.map((d) => (
          <FormRow key={d.label} label={d.label} value={d.value} />
        ))}

        {conditionCustomFields && conditionCustomFields.length > 0 && (
          <CustomFields
            groupName={t("UTP2.customConditionsQuestions")}
            fields={metaData?.SubstationConditionsGroup?.CustomQuestions}
          />
        )}
      </Card>
      {metaData?.CustomGroups && metaData?.CustomGroups.length > 0 && (
        <Card
          title={t("customQuestions.customQuestions")}
          className="mb-3"
          action={<CopyToClipboard textToCopy={constructClipboardText(undefined, metaData.CustomGroups)} />}
        >
          {metaData?.CustomGroups.map((customGroup) => (
            <CustomFields
              key={`customFields-${customGroup.group}`}
              groupName={customGroup.group}
              fields={customGroup.fields}
            />
          ))}
        </Card>
      )}
    </React.Fragment>
  );
}
